import React, { useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { ShieldCheck, Loader2, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Stat = ({ label, value, testid }) => (
  <div className="p-4 border border-border">
    <div className="sux-marker mb-1">{label}</div>
    <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }} data-testid={testid}>{value}</div>
  </div>
);

// Admin: AI daily-cap status, editable client message, and cost-safety counters.
export function AdminAiLimit() {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/ai-limit").then(({ data }) => { setData(data); setMessage(data.message); }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/ai-limit", { message });
      toast.success("Message enregistré.");
    } catch (err) {
      toast(err?.response?.data?.detail?.[0]?.msg || "Échec de l'enregistrement.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const hit = data.today_hits > 0;
  return (
    <section className="border border-[#002FA7] p-6 max-w-3xl mt-6" data-testid="admin-ai-limit">
      <div className="sux-marker mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Sécurité des coûts<InfoTip testid="admin-ai-limit-info" content={"Quand le budget IA du jour est dépassé, SUX1 bloque les nouveaux résumés et affiche votre message. Aucun client n'est débité pendant un blocage. Le budget se règle dans votre profil Emergent."} /></div>
      <h3 className="text-xl font-black tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>Limite IA du jour</h3>
      <div
        className={`p-4 mb-4 text-sm ${hit ? "bg-[#002FA7] text-white" : "bg-[#002FA7]/5 border border-[#002FA7]/30"}`}
        data-testid="admin-ai-limit-status"
      >
        {hit
          ? `Limite atteinte ${data.today_hits} fois aujourd'hui : des clients ont vu le message ci-dessous. Pour relever la limite : Emergent → Profil → Clé universelle → Limite d'utilisation des crédits.`
          : "Aucun blocage aujourd'hui. Les clients n'ont jamais été débités lors d'un blocage."}
      </div>
      <label htmlFor="ai-limit-msg" className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground block mb-2">
        Message affiché aux clients
      </label>
      <textarea
        id="ai-limit-msg"
        rows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="w-full border border-border px-4 py-3 text-sm bg-background focus:outline-none focus:border-[#002FA7]"
        data-testid="admin-ai-limit-message-input"
      />
      <div className="flex gap-2 mt-3">
        <button type="button" onClick={() => setMessage(data.default_message)} data-testid="admin-ai-limit-reset-btn"
          className="font-mono uppercase tracking-[0.2em] text-[11px] border border-border px-4 py-3 hover:border-[#002FA7] transition-colors flex items-center gap-2">
          <RotateCcw className="w-3 h-3" /> Par défaut
        </button>
        <button type="button" onClick={save} disabled={busy} data-testid="admin-ai-limit-save-btn"
          className="ml-auto font-mono uppercase tracking-[0.2em] text-[11px] bg-[#002FA7] text-white px-6 py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 flex items-center gap-2">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer
        </button>
      </div>
      <div className="grid grid-cols-2 gap-0 mt-6">
        <Stat label="Pro à vie" value={data.lifetime_pro_count} testid="admin-lifetime-pro-count" />
        <Stat label="Résumés offerts ce mois" value={data.lifetime_pro_used_this_month} testid="admin-lifetime-pro-used" />
        <Stat label="Paiements repris" value={data.clawbacks} testid="admin-clawbacks" />
        <Stat label="Comptes contestés" value={data.disputed_users} testid="admin-disputed-users" />
      </div>
    </section>
  );
}

export default AdminAiLimit;
